<template>
  <transition name="slide-fade">
    <div class="auth-container">
      <div class="auth-panel">
        <h2 class="auth-title">
          {{ showResetForm ? "Reset Password" : "Login" }}
        </h2>

        <p class="disclaimer">
          This symptom and behavior tracker is designed solely for your personal
          use. Any data that you choose to enter will not be accessed by us or
          shared with third parties.
        </p>

        <!-- Error Message -->
        <div v-if="errorMessage" class="error-message" role="alert">
          {{ errorMessage }}
        </div>

        <!-- Success Message -->
        <div v-if="successMessage" class="success-message" role="alert">
          {{ successMessage }}
        </div>

        <!-- Login Form -->
        <form
          v-if="!showResetForm"
          @submit.prevent="handleLogin"
          class="auth-form"
        >
          <!-- Google Sign In Button -->
          <button
            type="button"
            class="btn-google"
            @click="handleGoogleLogin"
            :disabled="loading"
          >
            <svg
              class="google-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 48 48"
              width="20"
              height="20"
            >
              <path
                fill="#FFC107"
                d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
              />
              <path
                fill="#FF3D00"
                d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
              />
              <path
                fill="#4CAF50"
                d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
              />
              <path
                fill="#1976D2"
                d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
              />
            </svg>
            <span v-if="!loading">Sign in with Google</span>
            <span v-else>Signing in...</span>
          </button>

          <!-- Divider -->
          <div class="divider">
            <span>or</span>
          </div>

          <!-- Email Field -->
          <div class="form-group">
            <label for="email">Email Address</label>
            <input
              id="email"
              v-model="email"
              type="email"
              required
              autocomplete="email"
              placeholder="your.email@example.com"
            />
          </div>

          <!-- Password Field -->
          <div class="form-group">
            <label for="password">Password</label>
            <input
              id="password"
              v-model="password"
              type="password"
              required
              autocomplete="current-password"
              placeholder="Enter your password"
            />
          </div>

          <!-- Submit Button -->
          <button type="submit" class="btn-primary" :disabled="loading">
            <span v-if="!loading">Login with Email</span>
            <span v-else>Logging in...</span>
          </button>
        </form>

        <!-- Password Reset Form -->
        <form
          v-if="showResetForm"
          @submit.prevent="handleReset"
          class="auth-form"
        >
          <!-- Email Field -->
          <div class="form-group">
            <label for="reset-email">Email Address</label>
            <input
              id="reset-email"
              v-model="email"
              type="email"
              required
              autocomplete="email"
              placeholder="your.email@example.com"
            />
            <p class="help-text">
              We'll send you a link to reset your password.
            </p>
          </div>

          <!-- Submit Button -->
          <button type="submit" class="btn-primary" :disabled="loading">
            <span v-if="!loading">Send Reset Link</span>
            <span v-else>Sending...</span>
          </button>

          <!-- Back to Login -->
          <div class="auth-footer">
            <button
              type="button"
              @click="
                showResetForm = false;
                errorMessage = null;
                successMessage = null;
              "
              class="link-button"
            >
              Back to Login
            </button>
          </div>
        </form>

        <!-- Forgot Password Link (only shown in login form) -->
        <div v-if="!showResetForm" class="auth-footer">
          <button
            type="button"
            @click="
              showResetForm = true;
              errorMessage = null;
            "
            class="link-button"
          >
            Forgot Password?
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import { mapMutations } from "vuex";
import { trackLogin, trackSessionStart } from "@/utils/analytics";

export default {
  name: "auth",
  data() {
    return {
      showResetForm: false,
      email: "",
      password: "",
      loading: false,
      errorMessage: null,
      successMessage: null,
    };
  },
  created() {
    this._authUnsubscribe = firebase.auth().onAuthStateChanged(async (user) => {
      if (user) {
        console.log("[Auth.vue] User authenticated:", user.uid);

        // Track successful login and session start
        const providerId =
          user.providerData &&
          user.providerData[0] &&
          user.providerData[0].providerId
            ? user.providerData[0].providerId
            : "password";
        await trackLogin(user.uid, providerId);
        await trackSessionStart(user.uid);

        // Check if there's a return URL from the static site
        const urlParams = new URLSearchParams(window.location.search);
        const returnUrl = urlParams.get("returnUrl");

        console.log("[Auth.vue] Return URL:", returnUrl);

        if (returnUrl) {
          // Redirect back to the static site page they came from
          const decodedUrl = decodeURIComponent(returnUrl);
          console.log("[Auth.vue] Redirecting to:", decodedUrl);
          window.location.href = decodedUrl;
        } else {
          // Default: go to app dashboard
          console.log("[Auth.vue] No return URL, going to dashboard");
          this.$router.push("/");
        }
      }
    });
  },
  beforeDestroy() {
    if (this._authUnsubscribe) this._authUnsubscribe();
  },
  methods: {
    ...mapMutations([
      "changePageTitle",
      "changePageIntroText",
      "modifyLoggedIn",
    ]),

    async handleLogin() {
      this.errorMessage = null;
      this.successMessage = null;
      this.loading = true;

      try {
        console.log("[Auth.vue] Logging in with email/password...");
        await firebase
          .auth()
          .signInWithEmailAndPassword(this.email, this.password);
        // Auth state change will handle redirect
      } catch (error) {
        console.error("[Auth.vue] Login error:", error);
        this.errorMessage = this.getErrorMessage(error.code);
      } finally {
        this.loading = false;
      }
    },

    async handleGoogleLogin() {
      this.errorMessage = null;
      this.successMessage = null;
      this.loading = true;

      try {
        console.log("[Auth.vue] Logging in with Google...");
        const provider = new firebase.auth.GoogleAuthProvider();
        await firebase.auth().signInWithPopup(provider);
        // Auth state change will handle redirect
      } catch (error) {
        console.error("[Auth.vue] Google login error:", error);
        this.errorMessage = this.getErrorMessage(error.code);
      } finally {
        this.loading = false;
      }
    },

    async handleReset() {
      // Reset messages
      this.errorMessage = null;
      this.successMessage = null;

      // Validate email
      if (!this.email) {
        this.errorMessage = "Please enter your email address.";
        return;
      }

      this.loading = true;

      try {
        console.log("[Auth.vue] Sending password reset email...");
        await firebase.auth().sendPasswordResetEmail(this.email);

        // Show success message
        this.successMessage =
          "Password reset email sent! Please check your inbox.";
        this.errorMessage = null;

        // Switch back to login form after 3 seconds
        setTimeout(() => {
          this.showResetForm = false;
          this.successMessage = null;
        }, 3000);
      } catch (error) {
        console.error("[Auth.vue] Password reset error:", error);
        this.errorMessage = this.getErrorMessage(error.code);
      } finally {
        this.loading = false;
      }
    },

    getErrorMessage(errorCode) {
      const messages = {
        "auth/invalid-email": "Please enter a valid email address.",
        "auth/user-disabled": "This account has been disabled.",
        "auth/user-not-found": "No account found with this email address.",
        "auth/wrong-password": "Incorrect password. Please try again.",
        "auth/invalid-credential":
          "Incorrect email or password. Please try again.",
        "auth/email-already-in-use":
          "An account with this email already exists.",
        "auth/weak-password": "Password should be at least 6 characters.",
        "auth/too-many-requests":
          "Too many failed attempts. Please try again later.",
        "auth/network-request-failed":
          "Network error. Please check your connection.",
        "auth/popup-closed-by-user": "Sign-in was cancelled. Please try again.",
        "auth/popup-blocked":
          "Pop-up was blocked by your browser. Please allow pop-ups and try again.",
        "auth/cancelled-popup-request":
          "Sign-in was cancelled. Please try again.",
        "auth/account-exists-with-different-credential":
          "An account already exists with this email using a different sign-in method.",
      };

      return messages[errorCode] || "An error occurred. Please try again.";
    },
  },
  mounted() {
    // Set logged in to false
    this.modifyLoggedIn(false);

    // Set the page title
    this.changePageTitle("Login");

    // Set page text
    this.changePageIntroText();
  },
};
</script>

<style scoped>
.auth-container {
  max-width: 500px;
  margin: 0 auto;
  padding: 2rem;
}

.auth-panel {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.auth-title {
  font-size: 1.5rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 1.5rem;
  color: #1a202c;
}

.disclaimer {
  line-height: 1.4;
  margin-bottom: 1.5rem;
  text-align: center;
  font-size: 0.875rem;
  color: #4a5568;
  padding: 0.75rem;
  background: #f7fafc;
  border-radius: 4px;
}

.error-message {
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #fed7d7;
  border: 1px solid #fc8181;
  color: #c53030;
  border-radius: 4px;
  font-size: 0.875rem;
}

.success-message {
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #c6f6d5;
  border: 1px solid #68d391;
  color: #22543d;
  border-radius: 4px;
  font-size: 0.875rem;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4a5568;
}

.form-group input {
  width: 100%;
  padding: 0.5rem 1rem;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  font-size: 1rem;
  transition: all 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: #3182ce;
  box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1);
}

.help-text {
  font-size: 0.75rem;
  color: #718096;
  margin-top: 0.25rem;
}

.btn-primary {
  width: 100%;
  background: #3182ce;
  color: white;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;
  margin-top: 0.5rem;
}

.btn-primary:hover:not(:disabled) {
  background: #2c5282;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-google {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  background: white;
  color: #3c4043;
  padding: 0.75rem 1rem;
  border: 1px solid #dadce0;
  border-radius: 4px;
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-google:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #c6c6c6;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.btn-google:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.google-icon {
  flex-shrink: 0;
}

.divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 0.5rem 0;
  color: #718096;
  font-size: 0.875rem;
}

.divider::before,
.divider::after {
  content: "";
  flex: 1;
  border-bottom: 1px solid #e2e8f0;
}

.divider span {
  padding: 0 0.75rem;
}

.auth-footer {
  margin-top: 1.5rem;
  text-align: center;
  font-size: 0.875rem;
  color: #4a5568;
}

.link-button {
  background: none;
  border: none;
  color: #3182ce;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  text-decoration: none;
}

.link-button:hover {
  text-decoration: underline;
}

/* Transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(10px);
  opacity: 0;
}
</style>
